import React, { useState, useEffect } from 'react';
import api from '../../api/axios'; // Use authenticated api instance instead of direct axios
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import '../../assets/fonts.css';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use the authenticated api instance instead of direct axios
        const { data } = await api.get('/notifications');
        setNotifications(data || []);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
        setError("Could not load notifications. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      );
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    } catch (err) {
      console.error("Failed to delete notification:", err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success':
        return 'border-green-200 bg-green-50';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50';
      case 'error':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-blue-200 bg-blue-50';
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Notifications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Notifications</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 font-inter">
      <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:px-0">
        <div className="mb-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-playfair font-bold text-gray-900 mb-2 tracking-tight">Notifications</h1>
              <p className="text-lg text-gray-500 font-inter">
                Stay updated with your project progress and important updates.
              </p>
            </div>
            {unreadCount > 0 && (
              <div className="bg-palamuru-blue text-white px-3 py-1 rounded-full text-sm font-medium shadow font-inter">
                {unreadCount} unread
              </div>
            )}
          </div>
        </div>
        {/* Notifications List */}
        <div className="space-y-5">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`relative bg-white rounded-2xl shadow-xl p-5 flex items-start gap-4 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl border-l-4 ${!notification.is_read ? 'border-palamuru-blue' : 'border-transparent'}`}
              >
                {/* Unread dot */}
                {!notification.is_read && (
                  <span className="absolute left-3 top-5 w-2 h-2 rounded-full bg-palamuru-blue animate-pulse" title="Unread"></span>
                )}
                {/* Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getNotificationIcon(notification.type)}
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-base text-gray-900 font-inter mb-1">{notification.title}</h3>
                    <span className="text-xs text-gray-400 whitespace-nowrap font-inter">{formatTime(notification.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-600 font-inter mb-1 break-words">{notification.message}</p>
                  {notification.category && (
                    <span className="inline-block bg-gray-100 px-2 py-1 rounded-full text-xs text-gray-500 mt-1 font-inter">
                      {notification.category}
                    </span>
                  )}
                </div>
                {/* Actions */}
                <div className="flex flex-col items-center gap-2 ml-4 mt-1">
                  {!notification.is_read && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="text-palamuru-blue hover:text-palamuru-gold transition-colors p-2 rounded-full shadow-sm bg-blue-50 hover:bg-blue-100"
                      title="Mark as read"
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteNotification(notification.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors p-2 rounded-full shadow-sm bg-gray-50 hover:bg-red-50"
                    title="Delete notification"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16">
              <BellIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2 font-playfair">No Notifications</h3>
              <p className="text-gray-600 font-inter">You're all caught up! No new notifications.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage; 